import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import { Container } from "@/components/container";
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from "@/partials/toolbar";
import { useLayout } from "@/providers";
import { DataGrid, KeenIcon } from "@/components";
import { useSnackbar } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@/components/modal";
import { formatDisplayDateTime } from "@/utils/Date";
import { PaginationState } from "@tanstack/react-table";
import {
  useGetSyntheticBookingCanaryConfigQuery,
  useUpdateSyntheticBookingCanaryConfigMutation,
  useGetCanaryStatusQuery,
  useGetCanaryRunsQuery,
  useTriggerCanaryRunMutation,
  useCancelCanaryRunMutation,
} from "@/services/BookingCanaryService";
import { CanaryRun, CanaryRunStatus } from "@/types/booking-canary.types";

const STATUS_POLLING_INTERVAL_MS = 10000;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const statusBadgeMap: Record<CanaryRunStatus, { label: string; variant: string }> =
  {
    RUNNING: { label: "Running", variant: "warning" },
    PASSED: { label: "Passed", variant: "success" },
    FAILED: { label: "Failed", variant: "danger" },
    CANCELLED: { label: "Cancelled", variant: "secondary" },
  };

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "object" && error !== null) {
    const typedError = error as {
      data?: { message?: string };
      message?: string;
    };

    if (typedError.data?.message) {
      return typedError.data.message;
    }

    if (typedError.message) {
      return typedError.message;
    }
  }

  return fallback;
};

const SyntheticCanary = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const canRead = hasPermission("synthetic_booking_canary_read");
  const canConfigure = hasPermission("synthetic_booking_canary_configure");
  const canRun = hasPermission("synthetic_booking_canary_run");

  // Settings form state
  const [enabled, setEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState("");
  // Comma-separated in the input; split into an array on save so multiple
  // recipients can be notified on failure.
  const [alertEmailsInput, setAlertEmailsInput] = useState("");
  const [intervalError, setIntervalError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const { data: configData, isLoading: isConfigLoading } =
    useGetSyntheticBookingCanaryConfigQuery(undefined, { skip: !canRead });
  const [updateConfig, { isLoading: isSaving }] =
    useUpdateSyntheticBookingCanaryConfigMutation();

  useEffect(() => {
    if (configData) {
      setEnabled(configData.enabled);
      setIntervalMinutes(String(configData.interval_minutes));
      setAlertEmailsInput((configData.alert_emails || []).join(", "));
    }
  }, [configData]);

  const isSettingsDirty = useMemo(() => {
    if (!configData) {
      return false;
    }

    return (
      enabled !== configData.enabled ||
      intervalMinutes !== String(configData.interval_minutes) ||
      alertEmailsInput !== (configData.alert_emails || []).join(", ")
    );
  }, [configData, enabled, intervalMinutes, alertEmailsInput]);

  const handleSaveSettings = async () => {
    const trimmedInterval = intervalMinutes.trim();
    const parsedInterval = Number(trimmedInterval);
    const isValidInterval =
      /^\d+$/.test(trimmedInterval) && parsedInterval > 0;

    const alertEmails = alertEmailsInput
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);
    const isValidEmails = alertEmails.every((email) =>
      EMAIL_REGEX.test(email)
    );

    setIntervalError(
      isValidInterval ? null : "Interval must be a positive integer"
    );
    setEmailError(
      isValidEmails
        ? null
        : "Enter valid, comma-separated email addresses"
    );

    if (!isValidInterval || !isValidEmails) {
      return;
    }

    try {
      await updateConfig({
        enabled,
        interval_minutes: parsedInterval,
        alert_emails: alertEmails,
      }).unwrap();
      enqueueSnackbar("Synthetic booking canary settings updated", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        getErrorMessage(error, "Failed to update canary settings"),
        { variant: "error" }
      );
    }
  };

  // Status + Run Now
  // Polls continuously for as long as this page is open, not just after
  // clicking "Run Now" - the cron can start a run at any time independent of
  // this admin session, so the status/history need to reflect that too.
  const { data: statusData, refetch: refetchStatus } = useGetCanaryStatusQuery(
    undefined,
    {
      skip: !canRead,
      pollingInterval: STATUS_POLLING_INTERVAL_MS,
    }
  );
  const [triggerCanaryRun, { isLoading: isTriggering }] =
    useTriggerCanaryRunMutation();
  const [cancelCanaryRun, { isLoading: isCancelling }] =
    useCancelCanaryRunMutation();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  const handleRunNow = async () => {
    try {
      await triggerCanaryRun().unwrap();
      enqueueSnackbar("Canary run triggered", { variant: "success" });
      refetchStatus();
      refetchRuns();
    } catch (error: unknown) {
      const typedError = error as { status?: number };
      const fallback =
        typedError?.status === 409
          ? "A canary run is already in progress"
          : "Failed to trigger canary run";
      enqueueSnackbar(getErrorMessage(error, fallback), { variant: "error" });
    }
  };

  const handleCancelRun = async () => {
    try {
      const result = await cancelCanaryRun().unwrap();
      enqueueSnackbar(
        result.cancelled
          ? "Canary run cancelled"
          : "No canary run was in progress",
        { variant: result.cancelled ? "success" : "info" }
      );
      refetchStatus();
      refetchRuns();
    } catch (error: unknown) {
      enqueueSnackbar(getErrorMessage(error, "Failed to cancel canary run"), {
        variant: "error",
      });
    } finally {
      setIsCancelConfirmOpen(false);
    }
  };

  // Run history
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {
    data: runsData,
    isLoading: isRunsLoading,
    refetch: refetchRuns,
  } = useGetCanaryRunsQuery(
    { page: pagination.pageIndex + 1, limit: pagination.pageSize },
    {
      skip: !canRead,
      pollingInterval: STATUS_POLLING_INTERVAL_MS,
    }
  );

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [selectedErrorOutput, setSelectedErrorOutput] = useState<
    string | null
  >(null);

  const handleViewError = useCallback((errorOutput: string | null) => {
    setSelectedErrorOutput(errorOutput);
    setIsErrorModalOpen(true);
  }, []);

  const handleCloseErrorModal = useCallback(() => {
    setIsErrorModalOpen(false);
    setSelectedErrorOutput(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "started_at",
        header: "Started At",
        cell: ({ row }: { row: { original: CanaryRun } }) =>
          formatDisplayDateTime(row.original.started_at, {
            includeSeconds: true,
          }),
      },
      {
        accessorKey: "finished_at",
        header: "Finished At",
        cell: ({ row }: { row: { original: CanaryRun } }) =>
          row.original.finished_at
            ? formatDisplayDateTime(row.original.finished_at, {
                includeSeconds: true,
              })
            : "-",
      },
      {
        accessorKey: "triggered_by",
        header: "Triggered By",
        cell: ({ row }: { row: { original: CanaryRun } }) =>
          row.original.triggered_by === "MANUAL" ? "Manual" : "Cron",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }: { row: { original: CanaryRun } }) => {
          const badge = statusBadgeMap[row.original.status];
          return (
            <span className={`badge badge-sm badge-${badge.variant}`}>
              {badge.label}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }: { row: { original: CanaryRun } }) =>
          row.original.status === "FAILED" ||
          row.original.status === "CANCELLED" ? (
            <button
              className="btn btn-sm btn-light btn-outline"
              onClick={() => handleViewError(row.original.error_output)}
              title="View Error Details"
              aria-label={`View error details for run ${row.original.id}`}
            >
              <KeenIcon icon="information-2" className="text-lg" />
              <span className="ml-1">View Error</span>
            </button>
          ) : null,
        meta: { className: "min-w-[150px]" },
      },
    ],
    [handleViewError]
  );

  if (!canRead) {
    return (
      <Fragment>
        {currentLayout?.name === "demo1-layout" && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle />
                <ToolbarDescription>
                  Synthetic booking canary
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="card">
              <div className="card-body">
                <div className="text-center py-4 text-gray-500">
                  You don't have permission to view the synthetic booking
                  canary.
                </div>
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  const isRunning = statusData?.isRunning ?? false;

  return (
    <Fragment>
      {currentLayout?.name === "demo1-layout" && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="synthetic-canary-heading" />
              <ToolbarDescription>
                Monitor and control the synthetic booking canary health check
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <span
                className={`badge badge-sm ${
                  isRunning ? "badge-warning" : "badge-success"
                }`}
              >
                {isRunning ? "Running" : "Idle"}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-primary flex items-center gap-2 h-10"
                onClick={() => void handleRunNow()}
                disabled={!canRun || isRunning || isTriggering}
              >
                {isTriggering ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Triggering...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <KeenIcon icon="arrows-circle" />
                    Run Now
                  </span>
                )}
              </button>
              {isRunning && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger flex items-center gap-2 h-10"
                  onClick={() => setIsCancelConfirmOpen(true)}
                  disabled={!canRun || isCancelling}
                >
                  {isCancelling ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Cancelling...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <KeenIcon icon="cross-circle" />
                      Cancel Run
                    </span>
                  )}
                </button>
              )}
            </ToolbarActions>
          </Toolbar>

          <div className="flex flex-col gap-6">
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Canary Settings</h2>
              </div>
              <div className="card-body">
                {isConfigLoading ? (
                  <div className="text-center py-4">
                    Loading canary settings...
                  </div>
                ) : (
                  <div className="space-y-6 max-w-md">
                    <div>
                      <label
                        id="canary-enabled-label"
                        htmlFor="canary-enabled-input"
                        className="form-label mb-3"
                      >
                        Enabled
                      </label>
                      <label className="switch">
                        <input
                          id="canary-enabled-input"
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setEnabled(e.target.checked)}
                          disabled={!canConfigure}
                          className="cursor-pointer"
                          aria-labelledby="canary-enabled-label"
                        />
                        <span className="slider round"></span>
                      </label>
                      <p className="text-gray-500 text-sm mt-1">
                        Controls whether the cron job runs the synthetic
                        booking canary checks.
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="canary-interval-input"
                        className="form-label"
                      >
                        Interval (minutes)
                      </label>
                      <input
                        id="canary-interval-input"
                        type="number"
                        min={1}
                        step={1}
                        value={intervalMinutes}
                        onChange={(e) => setIntervalMinutes(e.target.value)}
                        className="input input-bordered w-full"
                        disabled={!canConfigure}
                        aria-invalid={!!intervalError}
                        aria-describedby="canary-interval-error"
                      />
                      {intervalError && (
                        <p
                          id="canary-interval-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {intervalError}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="canary-email-input"
                        className="form-label"
                      >
                        Alert Emails
                      </label>
                      <input
                        id="canary-email-input"
                        type="text"
                        value={alertEmailsInput}
                        onChange={(e) => setAlertEmailsInput(e.target.value)}
                        placeholder="ops@example.com, dev@example.com"
                        className="input input-bordered w-full"
                        disabled={!canConfigure}
                        aria-invalid={!!emailError}
                        aria-describedby="canary-email-error"
                      />
                      <p className="text-gray-500 text-sm mt-1">
                        Comma-separated list of email addresses to notify on
                        failure.
                      </p>
                      {emailError && (
                        <p
                          id="canary-email-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {emailError}
                        </p>
                      )}
                    </div>

                    {canConfigure && (
                      <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                          className="btn btn-primary"
                          onClick={() => void handleSaveSettings()}
                          disabled={isSaving || !isSettingsDirty}
                        >
                          {isSaving ? (
                            <span className="flex items-center">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Saving...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <KeenIcon icon="check" className="mr-2" />
                              Save Settings
                            </span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Run History</h2>
                {statusData?.lastRunAt && (
                  <span className="text-sm text-gray-600">
                    Last run:{" "}
                    {formatDisplayDateTime(statusData.lastRunAt, {
                      includeSeconds: true,
                    })}
                  </span>
                )}
              </div>
              <div className="card-body">
                <DataGrid
                  columns={columns}
                  data={runsData?.data || []}
                  loading={isRunsLoading}
                  serverSide
                  pagination={{
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    totalItems: runsData?.total_items || 0,
                    totalPages: runsData?.total_pages || 0,
                    setPagination,
                  }}
                />
              </div>
            </div>
          </div>
        </Container>
      )}

      <Modal open={isErrorModalOpen} onClose={handleCloseErrorModal}>
        <ModalContent className="max-w-3xl min-w-[520px] min-h-[220px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fixed">
          <ModalHeader className="flex justify-between items-center border-b border-gray-200 p-5">
            <h1 className="text-lg font-semibold text-gray-900">
              Canary Run Error Details
            </h1>
            <button
              onClick={handleCloseErrorModal}
              className="btn btn-sm btn-icon btn-light"
              aria-label="Close modal"
            >
              <KeenIcon icon="cross" className="text-gray-600" />
            </button>
          </ModalHeader>
          <ModalBody className="p-5">
            {selectedErrorOutput ? (
              <pre className="whitespace-pre-wrap break-words text-red-800 bg-red-50 border border-red-200 rounded-lg p-4 max-h-[500px] overflow-y-auto">
                {selectedErrorOutput}
              </pre>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No error details available
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        open={isCancelConfirmOpen}
        onClose={() => setIsCancelConfirmOpen(false)}
      >
        <ModalContent className="max-w-md top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fixed">
          <ModalHeader className="flex justify-between items-center border-b border-gray-200 p-5">
            <h1 className="text-lg font-semibold text-gray-900">
              Cancel Canary Run
            </h1>
            <button
              onClick={() => setIsCancelConfirmOpen(false)}
              className="btn btn-sm btn-icon btn-light"
              aria-label="Close modal"
            >
              <KeenIcon icon="cross" className="text-gray-600" />
            </button>
          </ModalHeader>
          <ModalBody className="p-5">
            <p className="text-gray-700 mb-6">
              This will kill the in-progress canary run and mark it as
              cancelled. Are you sure?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setIsCancelConfirmOpen(false)}
                disabled={isCancelling}
              >
                Keep Running
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => void handleCancelRun()}
                disabled={isCancelling}
              >
                {isCancelling ? "Cancelling..." : "Cancel Run"}
              </button>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Fragment>
  );
};

export { SyntheticCanary };
